import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
  useToast,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import {
  AdminOrgDto,
  PersonDto,
  useAdminChangeOrganizationOwnerMutation,
  useListPersonsQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { Paging } from "../../../common/Paging";

interface Props {
  organization: AdminOrgDto;
  onClose: () => void;
}

export const AdminChangeOwnerModal = ({ organization, onClose }: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const [skip, setSkip] = useState(0);
  const limit = 10;

  const [searchName, setSearchName] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  const debouncedSetName = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedName(v.trim());
      }, 300),
    []
  );

  useEffect(() => {
    debouncedSetName(searchName);
    return () => {
      debouncedSetName.cancel();
    };
  }, [searchName, debouncedSetName]);

  useEffect(() => {
    setSkip(0);
  }, [debouncedName]);

  const { data, isLoading, isFetching } = useListPersonsQuery({
    orgIds: [organization.id],
    name: debouncedName ? debouncedName : undefined,
    limit,
    skip,
  });

  const persons = useMemo(() => data?.persons ?? [], [data]);
  const count = data?.count;

  const [changeOwner, { isLoading: isChangingOwner }] = useAdminChangeOrganizationOwnerMutation();
  const [personToConfirm, setPersonToConfirm] = useState<PersonDto>();
  const cancelConfirmationRef = useRef<HTMLButtonElement>(null);

  const confirmChangeOwner = async (person: PersonDto) => {
    try {
      await changeOwner({
        organizationId: authState.selectedOrg.id,
        orgId: organization.id,
        adminChangeOrganizationOwnerRequest: { newOwnerPersonId: person.id },
      }).unwrap();
      toast({ title: t("Owner changed!"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      setPersonToConfirm(undefined);
      apiErrorDisplayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflow="hidden">
        <ModalHeader>{t("Change owner")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" flex="1" minH="0" pb="4">
          <Stack spacing={3} flex="1" minH="0">
            <Text fontSize="sm" color="smMuted">
              {t("The new owner must already be a member of the organization. The current owner becomes an admin.")}
            </Text>
            <InputGroup size="sm">
              <InputLeftElement>
                <Icon as={FaSearch} color="smMuted" />
              </InputLeftElement>
              <Input
                placeholder={String(t("Search by name"))}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </InputGroup>
            <Box flex="1" overflowY="auto" minH="0">
              <Stack spacing={2}>
                {persons.map((p) => (
                  <Flex
                    key={p.id}
                    align="center"
                    justify="space-between"
                    border="1px solid"
                    borderColor="smBorder"
                    p={2}
                    rounded="md"
                  >
                    <Stack spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {p.email}
                      </Text>
                      <Text fontSize="xs" color="smMuted">
                        {p.firstName} {p.lastName}
                      </Text>
                    </Stack>
                    {p.id === organization.owner.id ? (
                      <Tag size="sm" rounded="full" bg="smBackgroundSecondary" color="smMuted" fontWeight="bold">
                        {t("Current owner")}
                      </Tag>
                    ) : (
                      <Button
                        size="xs"
                        colorScheme="teal"
                        variant="outline"
                        isDisabled={isChangingOwner}
                        onClick={() => setPersonToConfirm(p)}
                      >
                        {t("Make owner")}
                      </Button>
                    )}
                  </Flex>
                ))}
                {persons.length === 0 && !(isLoading || isFetching) ? (
                  <Text color="smMuted">{t("No users found")}</Text>
                ) : null}
              </Stack>
            </Box>
            <Paging
              limit={limit}
              skip={skip}
              setSkip={setSkip}
              isLoading={isLoading || isFetching}
              count={count || undefined}
            />
          </Stack>
        </ModalBody>
      </ModalContent>
      {personToConfirm && (
        <AlertDialog
          isOpen={true}
          leastDestructiveRef={cancelConfirmationRef}
          onClose={() => setPersonToConfirm(undefined)}
          closeOnOverlayClick={!isChangingOwner}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>{t("Transfer ownership?")}</AlertDialogHeader>
              <AlertDialogBody>
                {t("{{newOwner}} will become the owner of {{organization}}. {{currentOwner}} will become an admin.", {
                  newOwner: personDisplayName(personToConfirm),
                  organization: organization.name,
                  currentOwner: personDisplayName(organization.owner),
                })}
              </AlertDialogBody>
              <AlertDialogFooter gap="2">
                <Button
                  ref={cancelConfirmationRef}
                  size="sm"
                  variant="ghost"
                  isDisabled={isChangingOwner}
                  onClick={() => setPersonToConfirm(undefined)}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  size="sm"
                  colorScheme="teal"
                  isLoading={isChangingOwner}
                  onClick={() => confirmChangeOwner(personToConfirm)}
                >
                  {t("Transfer ownership")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Modal>
  );
};

const personDisplayName = (person: Pick<PersonDto, "firstName" | "lastName" | "email">) =>
  [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email;
